import { Canvas2DPaintContext } from "./Canvas2DPaintContext"
import { tessellateEdges } from "./edgeTessellation"
import type { IRenderer, PaintContext } from "./IRenderer"
import { LabelLayer } from "./LabelLayer"
import type { Scene } from "./Scene"
import type {
  EdgeBatch,
  LayoutDirection,
  RenderScene,
  RenderTimings,
} from "./types"
import { freeWasmBuffers } from "./wasmRuntime"

const DASH_DISABLE_SCALE = 0.5
const DASH_FLOW_SPEED = 28
const DIM_TRANSITION_SEC = 0.15
const EDGE_BASE_ALPHA = 0.6
const EDGE_DIM_ALPHA = 0.06
const EDGE_STRIDE_BYTES = 8 * 4
const DOT_STRIDE_BYTES = 7 * 4
const HOVER_GLOW_ALPHA = 0.18
const BLOOM_ALPHA = 0.16
const QUAD_VERTS = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1])

interface ProgramSet {
  grid: WebGLProgram
  edge: WebGLProgram
  dot: WebGLProgram
  ring: WebGLProgram
  glow: WebGLProgram
  halo: WebGLProgram
}

interface EdgeRange {
  color: string
  dash: number[]
  indexOffset: number
  indexCount: number
}

export class WebGLRenderer implements IRenderer {
  private glCanvas!: HTMLCanvasElement
  private overlayCanvas!: HTMLCanvasElement
  private gl!: WebGL2RenderingContext
  private overlayCtx!: CanvasRenderingContext2D
  private pc!: Canvas2DPaintContext
  private labelLayer!: LabelLayer
  private scene: Scene | null = null
  private colorCache = new Map<string, [number, number, number, number]>()
  private dpr = 1
  private width = 0
  private height = 0
  private programs!: ProgramSet
  private edgeVao: WebGLVertexArrayObject | null = null
  private edgeVbo: WebGLBuffer | null = null
  private edgeEbo: WebGLBuffer | null = null
  private dotVao: WebGLVertexArrayObject | null = null
  private dotInstanceBuffer: WebGLBuffer | null = null
  private ringVao: WebGLVertexArrayObject | null = null
  private nodeStateTexture: WebGLTexture | null = null
  private quadBuffer: WebGLBuffer | null = null
  private edgeRanges: EdgeRange[] = []
  private edgeIndexCount = 0
  private dotCount = 0
  private geometryDirty = true
  private geometryDirection: LayoutDirection | null = null
  private contextLost = false
  private reducedMotion = false
  private motionQuery: MediaQueryList | null = null
  private motionPreferenceDirty = false
  private dimT = 0
  private dimFrom = 0
  private dimTarget = 0
  private dimStartSec = 0
  private focusSelectedId: string | null = null
  private focusHighlightedIds: Set<string> | null = null
  private nodeStateData = new Uint8Array(1)
  private nodeStateTexWidth = 1
  private nodeStateTexHeight = 1
  private nodeStateDirty = true
  private hoverIdx = -1
  private hoverFrom = 0
  private hoverTarget = 0
  private hoverStartSec = 0
  private hoverPrevIdx = -1
  private hoverPrevFrom = 0
  private hoverPrevTarget = 0
  private hoverPrevStartSec = 0
  private motionActive = false

  lastRenderMs = 0
  lastCacheHit = false
  timings: RenderTimings = { clear: 0, grid: 0, edges: 0, nodes: 0, labels: 0 }

  private readonly onContextLost = (event: Event) => {
    event.preventDefault()
    this.contextLost = true
  }

  private readonly onContextRestored = () => {
    this.contextLost = false
    this.initGlResources()
    this.geometryDirty = true
  }

  private readonly onMotionPreferenceChange = (event: MediaQueryListEvent) => {
    this.reducedMotion = event.matches
    this.motionPreferenceDirty = true
  }

  init(container: HTMLElement) {
    this.glCanvas = document.createElement("canvas")
    this.overlayCanvas = document.createElement("canvas")
    for (const canvas of [this.glCanvas, this.overlayCanvas]) {
      canvas.style.position = "absolute"
      canvas.style.inset = "0"
    }

    container.appendChild(this.glCanvas)
    container.appendChild(this.overlayCanvas)

    const gl = this.glCanvas.getContext("webgl2", {
      alpha: false,
      antialias: true,
    })
    if (!gl) throw new Error("webgl2 unavailable")
    this.gl = gl
    this.overlayCtx = this.overlayCanvas.getContext("2d")!
    this.pc = new Canvas2DPaintContext(this.overlayCtx)
    this.labelLayer = new LabelLayer(this.overlayCtx, this.pc)
    this.dpr = devicePixelRatio || 1
    if (typeof matchMedia === "function") {
      this.motionQuery = matchMedia("(prefers-reduced-motion: reduce)")
      this.reducedMotion = this.motionQuery.matches
      this.motionQuery.addEventListener("change", this.onMotionPreferenceChange)
    }

    this.glCanvas.addEventListener("webglcontextlost", this.onContextLost)
    this.glCanvas.addEventListener(
      "webglcontextrestored",
      this.onContextRestored,
    )
    this.initGlResources()
  }

  resize(width: number, height: number) {
    this.dpr = devicePixelRatio || 1
    this.width = width
    this.height = height

    for (const canvas of [this.glCanvas, this.overlayCanvas]) {
      canvas.width = Math.round(width * this.dpr)
      canvas.height = Math.round(height * this.dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
    }

    if (!this.contextLost) {
      this.gl.viewport(0, 0, this.glCanvas.width, this.glCanvas.height)
    }
  }

  render(scene: RenderScene) {
    const t0 = performance.now()
    if (this.contextLost || this.width === 0 || this.height === 0) return

    this.updateEffectState(scene)

    const { gl } = this
    gl.viewport(0, 0, this.glCanvas.width, this.glCanvas.height)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

    const bg = hexToRgba(scene.config.background, 1)
    gl.clearColor(bg[0], bg[1], bg[2], bg[3])
    gl.clear(gl.COLOR_BUFFER_BIT)
    const tClear = performance.now()

    this.drawGrid(scene)
    const tGrid = performance.now()

    const rebuilt =
      this.geometryDirty ||
      this.geometryDirection !== scene.config.layoutDirection
    if (rebuilt) this.rebuildGeometry(scene)
    this.lastCacheHit = !rebuilt

    this.drawEdges(scene)
    const tEdges = performance.now()

    this.drawBloom(scene)
    this.drawHoverGlow(scene)
    this.drawDots(scene)
    this.drawAttention(scene)
    this.drawSelection(scene)
    const tNodes = performance.now()

    this.overlayCtx.save()
    this.overlayCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    this.overlayCtx.clearRect(0, 0, this.width, this.height)
    if (scene.config.showLabels) {
      const screenDotRadius =
        Math.max(6, 4 / scene.camera.scale) * scene.camera.scale
      this.labelLayer.draw(scene, screenDotRadius)
    }
    this.overlayCtx.restore()
    const tLabels = performance.now()

    this.timings = {
      clear: tClear - t0,
      grid: tGrid - tClear,
      edges: tEdges - tGrid,
      nodes: tNodes - tEdges,
      labels: tLabels - tNodes,
    }
    this.lastRenderMs = performance.now() - t0
  }

  getElement() {
    return this.overlayCanvas
  }

  getPaintContext(): PaintContext {
    return this.pc
  }

  destroy() {
    this.glCanvas.removeEventListener("webglcontextlost", this.onContextLost)
    this.glCanvas.removeEventListener(
      "webglcontextrestored",
      this.onContextRestored,
    )
    this.motionQuery?.removeEventListener(
      "change",
      this.onMotionPreferenceChange,
    )
    this.motionQuery = null
    if (!this.contextLost) this.deleteGlResources()
    this.glCanvas.remove()
    this.overlayCanvas.remove()
  }

  invalidateSceneCache() {
    this.geometryDirty = true
  }

  effectsActive() {
    const preferenceChanged = this.motionPreferenceDirty
    this.motionPreferenceDirty = false
    return preferenceChanged || this.motionActive || this.isDimAnimating()
  }

  setScene(scene: Scene) {
    this.scene = scene
    this.labelLayer.setScene(scene)
    this.resizeNodeStateStorage(scene.count)
    this.hoverIdx = -1
    this.hoverPrevIdx = -1
    this.hoverFrom = 0
    this.hoverTarget = 0
    this.hoverPrevFrom = 0
    this.hoverPrevTarget = 0
    // A same-size scene reuses the staging buffer; stale hover/dim bytes must
    // not leak onto the new scene's node indices.
    this.nodeStateData.fill(0)
    this.nodeStateDirty = true
    this.geometryDirty = true
  }

  private updateEffectState(scene: RenderScene) {
    const focusActive = this.focusActiveFor(scene)
    const focusChanged =
      scene.selectedId !== this.focusSelectedId ||
      scene.highlightedIds !== this.focusHighlightedIds

    if (focusChanged) {
      this.focusSelectedId = scene.selectedId
      this.focusHighlightedIds = scene.highlightedIds
      this.startDimTransition(focusActive ? 1 : 0, scene.timeSec)
      // Preserve stale R values while focus fades out; dimT eases them away.
      if (focusActive) this.writeNodeState(scene)
    }

    if (this.nodeStateDirty) this.writeNodeState(scene)
    this.updateDimTransition(scene.timeSec)
    this.updateHoverState(scene)

    this.motionActive =
      !this.reducedMotion &&
      (scene.selectedId !== null ||
        focusActive ||
        this.isDimAnimating() ||
        this.isHoverAnimating())
  }

  private startDimTransition(target: number, timeSec: number) {
    if (this.reducedMotion) {
      this.dimT = target
      this.dimTarget = target
      this.dimFrom = target
      this.dimStartSec = timeSec
      return
    }

    this.dimFrom = this.dimT
    this.dimTarget = target
    this.dimStartSec = timeSec
  }

  private updateDimTransition(timeSec: number) {
    if (this.reducedMotion) {
      this.dimT = this.dimTarget
      return
    }
    if (!this.isDimAnimating()) return

    const t = Math.min(1, (timeSec - this.dimStartSec) / DIM_TRANSITION_SEC)
    const eased = smoothstep(t)
    this.dimT = this.dimFrom + (this.dimTarget - this.dimFrom) * eased
    if (t >= 1) this.dimT = this.dimTarget
  }

  private updateHoverState(scene: RenderScene) {
    const nextIdx = scene.hoveredId
      ? (this.scene?.indexOf(scene.hoveredId) ?? -1)
      : -1
    if (nextIdx !== this.hoverIdx) {
      const currentStrength = this.hoverStrength(scene.timeSec)
      this.hoverPrevIdx = this.hoverIdx
      this.hoverPrevFrom = currentStrength
      this.hoverPrevTarget = 0
      this.hoverPrevStartSec = scene.timeSec
      this.hoverIdx = nextIdx
      this.hoverFrom = 0
      this.hoverTarget = nextIdx === -1 ? 0 : 1
      this.hoverStartSec = scene.timeSec
      if (this.reducedMotion) {
        this.hoverPrevIdx = -1
        this.hoverPrevFrom = 0
        this.hoverPrevTarget = 0
        this.hoverIdx = nextIdx
        this.hoverFrom = this.hoverTarget
      }
      this.writeHoverNodeState(scene, nextIdx)
    }
  }

  private hoverStrength(timeSec: number) {
    return this.easeValue(
      this.hoverFrom,
      this.hoverTarget,
      this.hoverStartSec,
      timeSec,
    )
  }

  private hoverPrevStrength(timeSec: number) {
    return this.easeValue(
      this.hoverPrevFrom,
      this.hoverPrevTarget,
      this.hoverPrevStartSec,
      timeSec,
    )
  }

  private easeValue(
    from: number,
    target: number,
    startSec: number,
    timeSec: number,
  ) {
    if (this.reducedMotion) return target
    const t = Math.min(1, (timeSec - startSec) / DIM_TRANSITION_SEC)
    return from + (target - from) * smoothstep(t)
  }

  private isDimAnimating() {
    return Math.abs(this.dimT - this.dimTarget) > 0.001
  }

  private isHoverAnimating() {
    return (
      Math.abs(
        this.hoverStrength(performance.now() / 1000) - this.hoverTarget,
      ) > 0.001 ||
      Math.abs(
        this.hoverPrevStrength(performance.now() / 1000) - this.hoverPrevTarget,
      ) > 0.001
    )
  }

  private focusActiveFor(scene: RenderScene) {
    return (
      scene.selectedId !== null ||
      (scene.highlightedIds !== null && scene.highlightedIds.size > 0)
    )
  }

  private isFocusedId(id: string, scene: RenderScene) {
    return id === scene.selectedId || scene.highlightedIds?.has(id) === true
  }

  private resizeNodeStateStorage(count: number) {
    const width = Math.max(1, Math.min(count, 2048))
    const height = Math.max(1, Math.ceil(count / width))
    this.nodeStateTexWidth = width
    this.nodeStateTexHeight = height
    const byteCount = Math.max(4, width * height * 4)
    if (this.nodeStateData.length !== byteCount) {
      this.nodeStateData = new Uint8Array(byteCount)
    }
  }

  private writeNodeState(scene: RenderScene) {
    const data = this.scene
    if (!data || !this.nodeStateTexture) return

    const focusActive = this.focusActiveFor(scene)
    for (let i = 0; i < data.count; i++) {
      const offset = i * 4
      this.nodeStateData[offset] =
        focusActive && !this.isFocusedId(data.ids[i], scene) ? 255 : 0
    }
    this.clearNodeStatePadding(data.count)
    this.uploadNodeState()
    this.nodeStateDirty = false
  }

  private writeHoverNodeState(scene: RenderScene, hoverIndex: number) {
    const data = this.scene
    if (!data || !this.nodeStateTexture) return

    for (let i = 0; i < data.count; i++) {
      const offset = i * 4
      this.nodeStateData[offset + 2] = this.nodeStateData[offset + 1]
      this.nodeStateData[offset + 1] = 0
    }

    if (hoverIndex >= 0 && hoverIndex < data.count) {
      this.nodeStateData[hoverIndex * 4 + 1] = 255
      for (const batch of scene.edgeBatches) {
        for (const edge of batch.edges) {
          if (edge.from === hoverIndex || edge.to === hoverIndex) {
            this.nodeStateData[edge.from * 4 + 1] = 255
            this.nodeStateData[edge.to * 4 + 1] = 255
          }
        }
      }
    }

    this.clearNodeStatePadding(data.count)
    this.uploadNodeState()
  }

  private clearNodeStatePadding(count: number) {
    for (let i = count * 4; i < this.nodeStateData.length; i++) {
      this.nodeStateData[i] = 0
    }
  }

  private uploadNodeState() {
    const { gl } = this
    if (!this.nodeStateTexture) return

    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.nodeStateTexture)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA8,
      this.nodeStateTexWidth,
      this.nodeStateTexHeight,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      this.nodeStateData,
    )
  }

  private bindNodeState(program: WebGLProgram) {
    const { gl } = this
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.nodeStateTexture)
    uniform1i(gl, program, "u_nodeState", 0)
    uniform2f(
      gl,
      program,
      "u_nodeStateSize",
      this.nodeStateTexWidth,
      this.nodeStateTexHeight,
    )
  }

  private initGlResources() {
    this.deleteGlResources()
    const { gl } = this
    this.programs = {
      grid: createProgram(gl, SHADER_SOURCES.grid.vs, SHADER_SOURCES.grid.fs),
      edge: createProgram(gl, SHADER_SOURCES.edge.vs, SHADER_SOURCES.edge.fs),
      dot: createProgram(gl, SHADER_SOURCES.dot.vs, SHADER_SOURCES.dot.fs),
      ring: createProgram(gl, SHADER_SOURCES.ring.vs, SHADER_SOURCES.ring.fs),
      glow: createProgram(gl, SHADER_SOURCES.glow.vs, SHADER_SOURCES.glow.fs),
      halo: createProgram(gl, SHADER_SOURCES.halo.vs, SHADER_SOURCES.halo.fs),
    }

    this.quadBuffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTS, gl.STATIC_DRAW)

    this.edgeVao = gl.createVertexArray()
    this.edgeVbo = gl.createBuffer()
    this.edgeEbo = gl.createBuffer()
    gl.bindVertexArray(this.edgeVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.edgeVbo)
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.edgeEbo)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, EDGE_STRIDE_BYTES, 0)
    gl.enableVertexAttribArray(1)
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, EDGE_STRIDE_BYTES, 8)
    gl.enableVertexAttribArray(2)
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, EDGE_STRIDE_BYTES, 16)
    gl.enableVertexAttribArray(3)
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, EDGE_STRIDE_BYTES, 20)
    gl.enableVertexAttribArray(4)
    gl.vertexAttribPointer(4, 2, gl.FLOAT, false, EDGE_STRIDE_BYTES, 24)

    this.dotVao = gl.createVertexArray()
    this.dotInstanceBuffer = gl.createBuffer()
    gl.bindVertexArray(this.dotVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.dotInstanceBuffer)
    gl.enableVertexAttribArray(1)
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, DOT_STRIDE_BYTES, 0)
    gl.vertexAttribDivisor(1, 1)
    gl.enableVertexAttribArray(2)
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, DOT_STRIDE_BYTES, 8)
    gl.vertexAttribDivisor(2, 1)
    gl.enableVertexAttribArray(3)
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, DOT_STRIDE_BYTES, 24)
    gl.vertexAttribDivisor(3, 1)

    this.ringVao = gl.createVertexArray()
    gl.bindVertexArray(this.ringVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)

    this.nodeStateTexture = gl.createTexture()
    gl.bindTexture(gl.TEXTURE_2D, this.nodeStateTexture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    this.nodeStateDirty = true

    gl.bindVertexArray(null)
    gl.bindBuffer(gl.ARRAY_BUFFER, null)
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null)
    gl.bindTexture(gl.TEXTURE_2D, null)
  }

  private deleteGlResources() {
    const gl = this.gl
    if (!gl) return
    for (const program of Object.values(this.programs ?? {})) {
      gl.deleteProgram(program)
    }
    gl.deleteVertexArray(this.edgeVao)
    gl.deleteVertexArray(this.dotVao)
    gl.deleteVertexArray(this.ringVao)
    gl.deleteBuffer(this.edgeVbo)
    gl.deleteBuffer(this.edgeEbo)
    gl.deleteBuffer(this.dotInstanceBuffer)
    gl.deleteBuffer(this.quadBuffer)
    gl.deleteTexture(this.nodeStateTexture)
    this.edgeVao = null
    this.dotVao = null
    this.ringVao = null
    this.edgeVbo = null
    this.edgeEbo = null
    this.dotInstanceBuffer = null
    this.quadBuffer = null
    this.nodeStateTexture = null
  }

  private drawGrid(scene: RenderScene) {
    const { gl } = this
    const { camera, config } = scene
    let gridSize = config.gridSize
    const screenGap = gridSize * camera.scale
    if (screenGap < 24) {
      gridSize *= 2 ** Math.ceil(Math.log2(40 / screenGap))
    } else if (screenGap > 96) {
      gridSize /= 2 ** Math.ceil(Math.log2(screenGap / 48))
    }
    if (gridSize * camera.scale < 8) return

    const dotSize = Math.max(1, Math.min(camera.scale * 1.5, 2.5))
    const color = hexToRgba(config.gridColor, 1)
    const program = this.programs.grid
    bindProgram(gl, program)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform1f(gl, program, "u_dpr", this.dpr)
    uniform1f(gl, program, "u_gridSize", gridSize)
    uniform1f(gl, program, "u_dotSize", dotSize)
    uniform4fv(gl, program, "u_color", color)
    gl.bindVertexArray(null)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  private rebuildGeometry(scene: RenderScene) {
    this.edgeRanges = []
    const data = this.scene
    if (!data?.sync()) {
      this.edgeIndexCount = 0
      this.dotCount = 0
      this.geometryDirty = true
      return
    }

    const tessEdges: { from: number; to: number }[] = []
    const groupCounts: number[] = []
    for (const batch of scene.edgeBatches) {
      groupCounts.push(batch.edges.length)
      for (const edge of batch.edges) tessEdges.push(edge)
    }

    const tessellated = tessellateEdges({
      edges: tessEdges,
      groupCounts,
      direction: scene.config.layoutDirection,
    })

    const gl = this.gl
    if (tessellated) {
      gl.bindVertexArray(this.edgeVao)
      gl.bindBuffer(gl.ARRAY_BUFFER, this.edgeVbo)
      gl.bufferData(gl.ARRAY_BUFFER, tessellated.verts, gl.STATIC_DRAW)
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.edgeEbo)
      gl.bufferData(
        gl.ELEMENT_ARRAY_BUFFER,
        tessellated.indices,
        gl.STATIC_DRAW,
      )
      this.edgeIndexCount = tessellated.indices.length
      this.buildEdgeRanges(scene.edgeBatches, tessellated.groupIndexCounts)
      // The returned arrays are views over wasm memory, invalid after free_all
      // or the next wasm call — free only once every view has been consumed.
      freeWasmBuffers()
      this.geometryDirection = scene.config.layoutDirection
      this.geometryDirty = false
    } else {
      this.edgeIndexCount = 0
      this.geometryDirty = true
    }

    // Fetch positions only after tessellateEdges: its alloc_input can grow
    // wasm memory, and growth detaches any previously derived view.
    const positions = data.positions()
    if (!positions) {
      this.dotCount = 0
      this.geometryDirty = true
      return
    }
    const dotData: number[] = []
    for (let i = 0; i < data.count; i++) {
      const color = this.rgbaFor(data.colors[i], 1)
      dotData.push(
        positions[i * 2],
        positions[i * 2 + 1],
        color[0],
        color[1],
        color[2],
        color[3],
        i,
      )
    }
    this.dotCount = data.count
    gl.bindVertexArray(this.dotVao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.dotInstanceBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(dotData), gl.STATIC_DRAW)
    gl.bindVertexArray(null)
  }

  private buildEdgeRanges(batches: EdgeBatch[], groupIndexCounts: Uint32Array) {
    let indexOffset = 0
    let groupIdx = 0
    this.edgeRanges = []
    for (const batch of batches) {
      const indexCount = groupIndexCounts[groupIdx++] ?? 0
      this.edgeRanges.push({
        color: batch.color,
        dash: batch.dash,
        indexOffset: indexOffset * 4,
        indexCount,
      })
      indexOffset += indexCount
    }
  }

  private drawEdges(scene: RenderScene) {
    if (this.edgeIndexCount === 0) return

    const { gl } = this
    const { camera, config } = scene
    const widthPx =
      camera.scale < 0.3
        ? config.edgeWidth * 0.3
        : config.edgeWidth * camera.scale
    const program = this.programs.edge
    bindProgram(gl, program)
    gl.bindVertexArray(this.edgeVao)
    this.bindNodeState(program)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform1f(gl, program, "u_halfWidth", widthPx / 2)
    uniform1f(gl, program, "u_time", this.reducedMotion ? 0 : scene.timeSec)
    uniform1f(gl, program, "u_flowSpeed", DASH_FLOW_SPEED)
    uniform1f(gl, program, "u_dimT", this.dimT)
    uniform1f(gl, program, "u_edgeBaseAlpha", EDGE_BASE_ALPHA)
    uniform1f(gl, program, "u_edgeDimAlpha", EDGE_DIM_ALPHA)
    uniform1f(gl, program, "u_hoverIdx", this.hoverIdx)
    uniform1f(gl, program, "u_hoverInT", this.hoverStrength(scene.timeSec))
    uniform1f(gl, program, "u_hoverPrevIdx", this.hoverPrevIdx)
    uniform1f(gl, program, "u_hoverOutT", this.hoverPrevStrength(scene.timeSec))
    uniform1f(gl, program, "u_dashFlow", this.reducedMotion ? 0 : 1)

    for (const range of this.edgeRanges) {
      this.drawEdgeRange(range, scene)
    }
    gl.bindVertexArray(null)
  }

  private drawEdgeRange(range: EdgeRange, scene: RenderScene) {
    if (range.indexCount === 0) return

    const { gl } = this
    const program = this.programs.edge
    const color = hexToRgba(range.color, 1)
    const dashEnabled =
      scene.camera.scale >= DASH_DISABLE_SCALE && range.dash.length > 0
    const dashOn = range.dash[0] ?? 0
    const dashOff = range.dash[1] ?? dashOn

    uniform4fv(gl, program, "u_color", color)
    uniform1f(gl, program, "u_dashEnabled", dashEnabled ? 1 : 0)
    uniform1f(gl, program, "u_dashOn", dashOn)
    uniform1f(gl, program, "u_dashPeriod", dashOn + dashOff)
    gl.drawElements(
      gl.TRIANGLES,
      range.indexCount,
      gl.UNSIGNED_INT,
      range.indexOffset,
    )
  }

  private drawBloom(scene: RenderScene) {
    if (this.dotCount === 0 || this.dimT <= 0) return

    const { gl } = this
    const { camera } = scene
    const radiusPx = Math.max(6, 4 / camera.scale) * camera.scale * 2.8
    const program = this.programs.glow
    bindProgram(gl, program)
    gl.bindVertexArray(this.dotVao)
    this.bindNodeState(program)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform1f(gl, program, "u_radius", radiusPx)
    uniform1f(gl, program, "u_dimT", this.dimT)
    uniform1f(gl, program, "u_bloomAlpha", BLOOM_ALPHA)
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.dotCount)
    gl.bindVertexArray(null)
  }

  private drawHoverGlow(scene: RenderScene) {
    if (!scene.hoveredId) return
    const data = this.scene
    if (!data) return
    const idx = data.indexOf(scene.hoveredId)
    const positions = data.positions()
    if (idx === undefined || !positions) return

    const { gl } = this
    const { camera } = scene
    const radiusPx = Math.max(6, 4 / camera.scale) * camera.scale * 2
    const color = this.rgbaFor(data.colors[idx], HOVER_GLOW_ALPHA)
    const program = this.programs.halo
    bindProgram(gl, program)
    gl.bindVertexArray(this.ringVao)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform2f(
      gl,
      program,
      "u_center",
      positions[idx * 2],
      positions[idx * 2 + 1],
    )
    uniform1f(gl, program, "u_radius", radiusPx)
    uniform4fv(gl, program, "u_color", color)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    gl.bindVertexArray(null)
  }

  private drawDots(scene: RenderScene) {
    if (this.dotCount === 0) return

    const { gl } = this
    const { camera } = scene
    const radiusPx = Math.max(6, 4 / camera.scale) * camera.scale
    const program = this.programs.dot
    bindProgram(gl, program)
    gl.bindVertexArray(this.dotVao)
    this.bindNodeState(program)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform1f(gl, program, "u_radius", radiusPx)
    uniform1f(gl, program, "u_dimAlpha", scene.config.dimAlpha)
    uniform1f(gl, program, "u_dimT", this.dimT)
    uniform1f(gl, program, "u_hoverInT", this.hoverStrength(scene.timeSec))
    uniform1f(gl, program, "u_hoverOutT", this.hoverPrevStrength(scene.timeSec))
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.dotCount)
    gl.bindVertexArray(null)
  }

  private drawAttention(scene: RenderScene) {
    if (!scene.attentionIds || scene.attentionIds.size === 0) return
    const data = this.scene
    const positions = data?.positions()
    if (!data || !positions) return

    const { gl } = this
    const { camera } = scene
    const radiusPx = Math.max(6, 4 / camera.scale) * camera.scale + 4
    const program = this.programs.ring
    bindProgram(gl, program)
    gl.bindVertexArray(this.ringVao)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform1f(gl, program, "u_radius", radiusPx)
    uniform1f(gl, program, "u_stroke", 2)
    uniform4fv(gl, program, "u_color", this.rgbaFor("#fbbf24", 0.95))

    for (const id of scene.attentionIds) {
      const idx = data.indexOf(id)
      if (idx === undefined) continue
      uniform2f(
        gl,
        program,
        "u_center",
        positions[idx * 2],
        positions[idx * 2 + 1],
      )
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    }
    gl.bindVertexArray(null)
  }

  private drawSelection(scene: RenderScene) {
    if (!scene.selectedId) return
    const data = this.scene
    const idx = data?.indexOf(scene.selectedId)
    const positions = data?.positions()
    if (idx === undefined || !positions) return

    const { gl } = this
    const { camera, config } = scene
    const pulse = this.reducedMotion ? 0 : Math.sin(scene.timeSec * 3.2)
    const radiusPx =
      Math.max(6, 4 / camera.scale) * camera.scale + 2 + pulse * 1.5
    const centerX = positions[idx * 2]
    const centerY = positions[idx * 2 + 1]
    const color = this.rgbaFor(
      config.selectionColor,
      this.reducedMotion ? 1 : 0.85 + pulse * 0.15,
    )
    const program = this.programs.ring

    bindProgram(gl, program)
    gl.bindVertexArray(this.ringVao)
    uniform2f(gl, program, "u_resolution", this.width, this.height)
    uniform2f(gl, program, "u_translate", camera.x, camera.y)
    uniform1f(gl, program, "u_scale", camera.scale)
    uniform2f(gl, program, "u_center", centerX, centerY)
    uniform1f(gl, program, "u_radius", radiusPx)
    uniform1f(gl, program, "u_stroke", 3)
    uniform4fv(gl, program, "u_color", color)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    gl.bindVertexArray(null)
  }

  private rgbaFor(color: string, alpha: number) {
    const key = `${color}|${alpha}`
    let rgba = this.colorCache.get(key)
    if (!rgba) {
      rgba = hexToRgba(color, alpha)
      this.colorCache.set(key, rgba)
    }
    return rgba
  }
}

function hexToRgba(
  color: string,
  alpha: number,
): [number, number, number, number] {
  const c = color.trim()
  if (c.startsWith("#")) {
    if (c.length === 4) {
      const r = Number.parseInt(c[1] + c[1], 16) / 255
      const g = Number.parseInt(c[2] + c[2], 16) / 255
      const b = Number.parseInt(c[3] + c[3], 16) / 255
      return [r, g, b, alpha]
    }
    const hex = c.slice(1, 7)
    const n = Number.parseInt(hex, 16)
    return [
      ((n >> 16) & 255) / 255,
      ((n >> 8) & 255) / 255,
      (n & 255) / 255,
      alpha,
    ]
  }

  const rgba = c.match(/rgba?\(([^)]+)\)/)
  if (rgba) {
    const parts = rgba[1].split(",").map((p) => Number.parseFloat(p.trim()))
    return [
      (parts[0] ?? 0) / 255,
      (parts[1] ?? 0) / 255,
      (parts[2] ?? 0) / 255,
      (parts[3] ?? 1) * alpha,
    ]
  }

  return [1, 1, 1, alpha]
}

function createProgram(
  gl: WebGL2RenderingContext,
  vsSource: string,
  fsSource: string,
) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource)
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource)
  const program = gl.createProgram()
  if (!program) throw new Error("failed to create WebGL program")
  gl.attachShader(program, vs)
  gl.attachShader(program, fs)
  gl.linkProgram(program)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? "unknown link error"
    gl.deleteProgram(program)
    throw new Error(log)
  }
  return program
}

function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
) {
  const shader = gl.createShader(type)
  if (!shader) throw new Error("failed to create WebGL shader")
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? "unknown shader error"
    gl.deleteShader(shader)
    throw new Error(log)
  }
  return shader
}

function bindProgram(gl: WebGL2RenderingContext, program: WebGLProgram) {
  // biome-ignore lint/correctness/useHookAtTopLevel: WebGL API, not a React hook.
  gl.useProgram(program)
}

function uniform1f(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  x: number,
) {
  gl.uniform1f(gl.getUniformLocation(program, name), x)
}

function uniform1i(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  x: number,
) {
  gl.uniform1i(gl.getUniformLocation(program, name), x)
}

function uniform2f(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  x: number,
  y: number,
) {
  gl.uniform2f(gl.getUniformLocation(program, name), x, y)
}

function uniform4fv(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  name: string,
  v: [number, number, number, number],
) {
  gl.uniform4fv(gl.getUniformLocation(program, name), v)
}

function smoothstep(t: number) {
  return t * t * (3 - 2 * t)
}

const GRID_VS = `#version 300 es
void main() {
  vec2 p = vec2((gl_VertexID == 1) ? 3.0 : -1.0, (gl_VertexID == 2) ? 3.0 : -1.0);
  gl_Position = vec4(p, 0.0, 1.0);
}
`

const GRID_FS = `#version 300 es
precision highp float;
uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_dpr;
uniform float u_gridSize;
uniform float u_dotSize;
uniform vec4 u_color;
out vec4 outColor;
void main() {
  vec2 screen = vec2(gl_FragCoord.x / u_dpr, u_resolution.y - gl_FragCoord.y / u_dpr);
  vec2 world = (screen - u_translate) / u_scale;
  vec2 cell = floor(world / u_gridSize);
  vec2 anchor = cell * u_gridSize * u_scale + u_translate;
  vec2 local = screen - anchor;
  if (local.x >= 0.0 && local.y >= 0.0 && local.x < u_dotSize && local.y < u_dotSize) {
    outColor = u_color;
  } else {
    discard;
  }
}
`

const EDGE_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec2 a_normal;
layout(location = 2) in float a_side;
layout(location = 3) in float a_arclen;
layout(location = 4) in vec2 a_nodes;
uniform sampler2D u_nodeState;
uniform vec2 u_nodeStateSize;
uniform float u_dimT;
uniform float u_hoverIdx;
uniform float u_hoverInT;
uniform float u_hoverPrevIdx;
uniform float u_hoverOutT;
uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_halfWidth;
out float v_dist;
out float v_arclen;
out float v_dim;
out float v_boost;
float nodeState(float idx) {
  float w = u_nodeStateSize.x;
  ivec2 uv = ivec2(int(mod(idx, w)), int(floor(idx / w)));
  return texelFetch(u_nodeState, uv, 0).r;
}
float matchesNode(vec2 edgeNodes, float idx) {
  if (idx < 0.0) return 0.0;
  return max(1.0 - step(0.5, abs(edgeNodes.x - idx)), 1.0 - step(0.5, abs(edgeNodes.y - idx)));
}
void main() {
  vec2 screen = a_pos * u_scale + u_translate + a_normal * a_side * (u_halfWidth + 1.0);
  vec2 clip = (screen / u_resolution * 2.0 - 1.0) * vec2(1.0, -1.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_dist = a_side * (u_halfWidth + 1.0);
  v_arclen = a_arclen;
  v_dim = max(nodeState(a_nodes.x), nodeState(a_nodes.y));
  v_boost = max(matchesNode(a_nodes, u_hoverIdx) * u_hoverInT, matchesNode(a_nodes, u_hoverPrevIdx) * u_hoverOutT);
}
`

const EDGE_FS = `#version 300 es
precision highp float;
uniform vec4 u_color;
uniform float u_scale;
uniform float u_halfWidth;
uniform float u_dashEnabled;
uniform float u_dashFlow;
uniform float u_dashOn;
uniform float u_dashPeriod;
uniform float u_time;
uniform float u_flowSpeed;
uniform float u_dimT;
uniform float u_edgeBaseAlpha;
uniform float u_edgeDimAlpha;
in float v_dist;
in float v_arclen;
in float v_dim;
in float v_boost;
out vec4 outColor;
void main() {
  if (u_dashEnabled > 0.5) {
    float dashScale = max(u_scale, 1.0);
    // Binary-gate the flow: scaling absolute time by a ramping gate would
    // sweep the dash phase by (time * speed) during the dim fade — a strobe
    // that gets worse the longer the page is open.
    float flow = step(0.5, u_dashFlow * u_dimT * (1.0 - v_dim)) * u_time * u_flowSpeed;
    float m = mod(v_arclen * u_scale - flow, u_dashPeriod * dashScale);
    if (m > u_dashOn * dashScale) discard;
  }
  float aa = 1.0 - smoothstep(u_halfWidth - 0.5, u_halfWidth + 0.5, abs(v_dist));
  float focusAlpha = mix(1.0, u_edgeDimAlpha, v_dim);
  float alpha = mix(u_edgeBaseAlpha, focusAlpha, u_dimT);
  alpha = mix(alpha, 1.0, v_boost);
  outColor = vec4(u_color.rgb, u_color.a * alpha * aa);
}
`

const DOT_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_quad;
layout(location = 1) in vec2 i_center;
layout(location = 2) in vec4 i_color;
layout(location = 3) in float i_node;
uniform sampler2D u_nodeState;
uniform vec2 u_nodeStateSize;
uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_radius;
uniform float u_hoverInT;
uniform float u_hoverOutT;
out vec2 v_quad;
out vec4 v_color;
out float v_dim;
out float v_boost;
void main() {
  vec2 screen = i_center * u_scale + u_translate + a_quad * (u_radius + 1.0);
  vec2 clip = (screen / u_resolution * 2.0 - 1.0) * vec2(1.0, -1.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_quad = a_quad;
  v_color = i_color;
  float w = u_nodeStateSize.x;
  ivec2 uv = ivec2(int(mod(i_node, w)), int(floor(i_node / w)));
  vec4 state = texelFetch(u_nodeState, uv, 0);
  v_dim = state.r;
  v_boost = max(state.g * u_hoverInT, state.b * u_hoverOutT);
}
`

const DOT_FS = `#version 300 es
precision highp float;
uniform float u_radius;
uniform float u_dimAlpha;
uniform float u_dimT;
in vec2 v_quad;
in vec4 v_color;
in float v_dim;
in float v_boost;
out vec4 outColor;
void main() {
  float dist = length(v_quad) * (u_radius + 1.0);
  float aa = 1.0 - smoothstep(u_radius - 0.5, u_radius + 0.5, dist);
  float alpha = mix(mix(1.0, u_dimAlpha, v_dim * u_dimT), 1.0, v_boost);
  outColor = vec4(v_color.rgb, v_color.a * alpha * aa);
}
`

const RING_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_quad;
uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform vec2 u_center;
uniform float u_radius;
uniform float u_stroke;
out vec2 v_quad;
out float v_extent;
void main() {
  float extent = u_radius + u_stroke * 0.5 + 1.0;
  vec2 screen = u_center * u_scale + u_translate + a_quad * extent;
  vec2 clip = (screen / u_resolution * 2.0 - 1.0) * vec2(1.0, -1.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_quad = a_quad;
  v_extent = extent;
}
`

const RING_FS = `#version 300 es
precision highp float;
uniform float u_radius;
uniform float u_stroke;
uniform vec4 u_color;
in vec2 v_quad;
in float v_extent;
out vec4 outColor;
void main() {
  float dist = length(v_quad) * v_extent;
  float delta = abs(dist - u_radius);
  float halfStroke = u_stroke * 0.5;
  float aa = 1.0 - smoothstep(halfStroke - 0.5, halfStroke + 0.5, delta);
  outColor = vec4(u_color.rgb, u_color.a * aa);
}
`

const GLOW_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_quad;
layout(location = 1) in vec2 i_center;
layout(location = 2) in vec4 i_color;
layout(location = 3) in float i_node;
uniform sampler2D u_nodeState;
uniform vec2 u_nodeStateSize;
uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_radius;
uniform float u_dimT;
out vec2 v_quad;
out vec4 v_color;
out float v_dim;
void main() {
  float w = u_nodeStateSize.x;
  ivec2 uv = ivec2(int(mod(i_node, w)), int(floor(i_node / w)));
  v_dim = texelFetch(u_nodeState, uv, 0).r;
  float strength = u_dimT * (1.0 - v_dim);
  vec2 screen = i_center * u_scale + u_translate + a_quad * u_radius * strength;
  vec2 clip = (screen / u_resolution * 2.0 - 1.0) * vec2(1.0, -1.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_quad = a_quad;
  v_color = i_color;
}
`

const GLOW_FS = `#version 300 es
precision highp float;
uniform float u_dimT;
uniform float u_bloomAlpha;
in vec2 v_quad;
in vec4 v_color;
in float v_dim;
out vec4 outColor;
void main() {
  float d = length(v_quad);
  float falloff = pow(1.0 - smoothstep(0.0, 1.0, d), 2.0);
  outColor = vec4(v_color.rgb, v_color.a * u_bloomAlpha * u_dimT * (1.0 - v_dim) * falloff);
}
`

const HALO_VS = `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_quad;
uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform vec2 u_center;
uniform float u_radius;
out vec2 v_quad;
void main() {
  vec2 screen = u_center * u_scale + u_translate + a_quad * u_radius;
  vec2 clip = (screen / u_resolution * 2.0 - 1.0) * vec2(1.0, -1.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_quad = a_quad;
}
`

const HALO_FS = `#version 300 es
precision highp float;
uniform vec4 u_color;
in vec2 v_quad;
out vec4 outColor;
void main() {
  float d = length(v_quad);
  float falloff = pow(1.0 - smoothstep(0.0, 1.0, d), 2.0);
  outColor = vec4(u_color.rgb, u_color.a * falloff);
}
`

export const SHADER_SOURCES = {
  grid: { vs: GRID_VS, fs: GRID_FS },
  edge: { vs: EDGE_VS, fs: EDGE_FS },
  dot: { vs: DOT_VS, fs: DOT_FS },
  ring: { vs: RING_VS, fs: RING_FS },
  glow: { vs: GLOW_VS, fs: GLOW_FS },
  halo: { vs: HALO_VS, fs: HALO_FS },
} as const
